<template>
  <div>
    <CustomScrollbar v-if="isAuthenticated" />
    <MainNav v-if="isAuthenticated" />
    <div v-if="isAuthenticated" class="container top-row">
      <div id="logo">
        <router-link class="not_bubbled" to="/">Andreas S. König</router-link>
      </div>
      <DarkModeToggle />
    </div>

    <div class="content main">
      <router-view v-slot="{ Component }" class="inner">
        <transition name="fade" mode="out-in">
          <!-- 'key="$route.path"' wurde entfernt, um die Wiederverwendung der Komponente zu ermöglichen -->
          <component :is="Component"></component>
        </transition>
      </router-view>
      <div v-if="isAuthenticated" class="spacer"></div>
    </div>

    <Footer v-if="isAuthenticated" />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, provide } from "vue";
import api from "@/api";
import { useRouter } from "vue-router";
import { EventBus } from "@/utils/eventBus.js";

import DarkModeToggle from "@/components/DarkModeToggle.vue";
import Footer from "@/components/Footer.vue";
import MainNav from "@/components/MainNav.vue";
import CustomScrollbar from "@/components/CustomScrollbar.vue";

const router = useRouter();

// Reactive state for authentication and user data
const isAuthenticated = ref(false);
const currentUser = ref(null); // New reactive state for user data

// Provide reactive states to all child components
provide('isAuthenticated', isAuthenticated);
provide('currentUser', currentUser);

// Function to check token and load user data
// This function is now simplified and retrieves all necessary data in one step.
async function checkAuthentication() {
  const token = localStorage.getItem("token");
  const tokenExpire = localStorage.getItem("tokenExpire");

  // If no token exists or the token has expired
  if (!token || (tokenExpire && Date.now() >= parseInt(tokenExpire))) {
    localStorage.removeItem("token");
    localStorage.removeItem("tokenExpire");
    isAuthenticated.value = false;
    currentUser.value = null; // Clear user data
    router.push("/login");
    return;
  }

  try {
    // Validate the token and load exactly the relations the app needs.
    // Explicit populate instead of a deep-populate plugin: /users/me belongs to
    // users-permissions, and only `favourites` (rendered as project cards) plus
    // `cant_see` (visibility filter) are required here.
    // The Authorization header is attached by the api request interceptor.
    const response = await api.get(
      "/users/me?populate[favourites][populate][0]=thumbnail" +
        "&populate[favourites][populate][1]=header" +
        "&populate[favourites][populate][2]=categories" +
        "&populate[cant_see][fields][0]=slug"
    );

    isAuthenticated.value = true;
    // Assign the entire response directly to currentUser.value,
    // as it already contains the deep data.
    if (response && response.data) {
      currentUser.value = response.data;
      //console.log("User data with deep populate successfully retrieved:", currentUser.value);
    } else {
      currentUser.value = null;
      console.warn("User data not in expected format or empty.");
    }

  } catch (error) {
    // If the server rejects the token (e.g., invalid, expired)
    console.error("Authentication check failed:", error);
    localStorage.removeItem("token");
    localStorage.removeItem("tokenExpire");
    isAuthenticated.value = false;
    currentUser.value = null; // Clear user data
    router.push("/login");
  }
}

// Check authentication on component mount and register EventBus listener
onMounted(() => {
  checkAuthentication();

  // When "recheck-auth" is triggered, re-validate token and load user data
  EventBus.on("recheck-auth", checkAuthentication);
});

// Clean up when component leaves the DOM
onBeforeUnmount(() => {
  EventBus.off("recheck-auth", checkAuthentication);
});
</script>

<style lang="scss">
//@import '@/../mdb/scss/index.free.scss';
//@import "~@/assets/scss/variables";
//@import "~@/assets/scss/inital";
//@import "~@/assets/scss/dark";
//@import "~@/assets/scss/mixins";

@use "~@/assets/scss/variables" as *;
@use "~@/assets/scss/inital" as *;
@use "~@/assets/scss/dark" as *;
@use "~@/assets/scss/mixins" as *;



html,
body {
  height: 100%;
}

/* Hide default scrollbar for Webkit browsers (Chrome, Safari) */
body::-webkit-scrollbar {
  display: none;
}

/* Hide default scrollbar for Firefox */
body {
  scrollbar-width: none;
  /* Firefox */
}

html,
#app {
  position: relative;
}

#app {
  /* Hide default scrollbar */

  scrollbar-width: none;
  /* For Firefox */


  &::-webkit-scrollbar {
    display: none;
    /* For Webkit browsers (Chrome, Safari) */
  }
}

#logo {
  z-index: 9999;
  opacity: 0.7;
  font-weight: 800;
  text-transform: uppercase;

  &:hover {
    opacity: 1;

    a {
      color: #7532fa;
    }
  }
}

.top-row {
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  top: 1em;
  left: 0em;
  right: 0em;

}

h1,
h3 {
  color: #1d1d1d;
}

h2 {
  color: #712cf9
}

h1,
h2,
h3 {
  font-style: normal;
  font-weight: 800;
  line-height: 115%;
}

h2 {
  line-height: 1.25em;
}

h3 {
  line-height: 120%;
}

h4 {
  line-height: 150%;
}

h1,
h2,
h3,
h4,
h5,
h6 {
  margin-bottom: 0.75em;
}

h1 {
  //font-weight: 300;
  font-size: clamp(1.75rem, 5vw, 3rem);
  text-transform: uppercase;
  margin-bottom: 0.25em;
}

.spacer {
  padding-top: 6em;
}

h2 {
  font-weight: 600;
  font-size: clamp(1.5rem, 3.5vw, 1.75rem);
}

h3 {
  font-weight: 400;
  font-size: clamp(1.25rem, 2.5vw, 1.5rem);
}

h4 {
  font-weight: 400;
  font-size: clamp(1.15rem, 2vw, 1.25rem);
  margin-bottom: 0.75em;
}

h5,
h6 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75em;
  font-size: clamp(1rem, 2vw, 1.15rem);
}

p,
span {
  font-size: clamp(1rem, 2vw, 1.15rem);

  a {
    font-size: clamp(1rem, 2vw, 1.15rem);
  }
}

body {
  padding: 0;
  margin: 0;

}

* {
  font-family: 'Montserrat', sans-serif;

}

a {
  color: black;
  text-decoration: none;
}

.no-padding {
  padding: 0 !important;
}

.markdown-body {
  *:last-child {
    margin-bottom: 0 !important;
  }
}

.markdown-body {
  max-width: 800px;
  margin: 0 auto;
}

* {
  &:not(.markdown-body) {
    >ul {
      list-style: none;
      padding: 0;
    }
  }
}


.content {
  padding: 0;
  padding-top: 7.5rem;
  margin: 0;
  display: block;
  min-width: 100%;
  min-height: 100%;
  min-height: 100vh;
  z-index: 2;
  position: relative;
  background-color: white;
  //padding-bottom: 2em;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);

  .inner {
    padding-bottom: 6em;
  }
}

.content-inner {
  width: 80%;
  max-width: 1280px;
  // margin: 0 auto;
}

.centerXY {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.centerX {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.rounded-wrapper {
  will-change: height;
  width: 100%;
  position: relative;
  height: 10vh;
  overflow: hidden;
  z-index: 2;
}

.rounded-div {
  width: 150%;
  content: "";
  display: block;
  position: absolute;
  background: white;
  height: 750%;
  left: 50%;
  border-radius: 50%;
  transform: translate(-50%, -86.666%);
  z-index: 1;
}

.container {
    .container {
      padding: 0 0 !important;
    }
}

@media (max-width: 767px) {

  .container {
    padding: 0 1.5rem;
  }

  .rounded-wrapper {
    .rounded-div {
      height: 500%
    }
  }

  .media-grid {
    .flex {
      .f-item {
        &:not(:first-of-type) {
          padding-top: 2rem;
        }
      }
    }
  }

}

@media (max-width: 991px) {

  .container-sm,
  .container {
    max-width: inherit !important;
    padding: 0 4vw !important;
  }
}

/* Custom Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.8s ease-in-out; /* Longer duration and smoother curve */
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
